import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { delay, finalize, map } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { DropdownItemsService } from 'src/app/shared/services/dropdown-items.service';
import { blankScenarioTemplatePreview } from '../mock/blank-scenario-template-preview.const';
import { Scenario } from '../models';
import { ScenarioTemplate } from '../models/scenario-template.model';
import { ScenariosService } from '../scenarios.service';

@Component({
  selector: 'app-scenario-wizard',
  templateUrl: './scenario-wizard.component.html',
  styleUrls: ['./scenario-wizard.component.scss'],
})
export class ScenarioWizardComponent implements OnDestroy {
  sites: Choice[];
  duplicate: boolean;
  form = this.formBuilder.group({
    selectedSiteId: [null, Validators.required],
    scenarioName: [null, Validators.required],
  });

  get siteCtrl() {
    return this.form?.get('selectedSiteId') as FormControl;
  }

  private blankScenario: ScenarioTemplate = {
    id: 'blank',
    preview: null,
    previewURL: `url(${blankScenarioTemplatePreview})`,
    name: this.basicService.translate('scenario-wizard/scenario-blank-scenario-name'),
    description: this.basicService.translate('scenario-wizard/scenario-blank-scenario-description'),
  };

  templates$: Observable<ScenarioTemplate[]> = of([this.blankScenario]);
  scenarioFlavor: 'quick-replies' | 'campaign-scenarios';
  backToLabel = this.basicService.translate('scenario-wizard/back-to-list');
  subscriptions: Subscription = new Subscription();
  scenarioTemplatesLoading: boolean;
  selectedScenarioTemplate: number | 'blank';
  sitesWithQuickRepliesEnabled: SiteV2[];
  scenarios: Scenario[];
  scenario: Scenario;
  warningNameAlreadyExists = false;

  constructor(
    private scenariosService: ScenariosService,
    private basicService: BasicService,
    private sitesService: SitesService,
    private formBuilder: FormBuilder,
    private dropdownItemsService: DropdownItemsService,
    private customRouter: CustomRouter,
    alcmeonContextService: AlcmeonContextService,
    @Inject('SiteFilterHelperFactory') siteFilterHelperFactory: any
  ) {
    const { isMarketingMode, duplicate } = customRouter.current.data;

    this.duplicate = duplicate;

    this.scenarioFlavor = isMarketingMode ? 'campaign-scenarios' : 'quick-replies';
    this.scenariosService.flavor = this.scenarioFlavor;
    const sitesWithQuickRepliesEnabled = this.sitesService.sitesV2.filter((site: SiteV2) => site.quick_replies_enabled);
    const requiredCapability =
      this.scenarioFlavor === 'campaign-scenarios' ? CapabilityIdEnum.Marketing : CapabilityIdEnum.Automation;
    const sitesThatHaveCapability = siteFilterHelperFactory.filterSitesThatHaveCapability(
      sitesWithQuickRepliesEnabled,
      alcmeonContextService.self,
      requiredCapability
    );

    this.sites = this.dropdownItemsService.buildSiteItems(sitesThatHaveCapability);
    (async () => {
      const scenarios: Scenario[] = await this.scenariosService.findAll().toPromise();
      this.scenarios = scenarios;
      if (duplicate) {
        const { scenarioId } = customRouter.params;
        this.scenario = (scenarios || []).find(({ id }) => id === scenarioId);
        const { name } = this.scenario;
        this.form.patchValue({
          selectedSiteId: this.scenario.site_id,
          scenarioName: `${this.basicService.translate(`${this.scenarioFlavor}/prefix/copy-of`)} ${name}`,
        });
      } else {
        this.subscriptions.add(
          this.siteCtrl.valueChanges.subscribe(siteId => {
            this.selectedScenarioTemplate = null;
            this.scenarioTemplatesLoading = true;
            this.templates$ = scenariosService
              .getScenarioTemplates(sitesService.sitesV2Map.get(siteId)?.type, this.scenarioFlavor)
              .pipe(
                delay(500),
                map(result => {
                  const templates = [this.blankScenario].concat(result || []);
                  this.selectedScenarioTemplate = 'blank';
                  return templates;
                }),
                finalize(() => (this.scenarioTemplatesLoading = false))
              );
          })
        );
      }
    })();
  }

  goBack() {
    this.customRouter.go(`auth.alcmeon.fuse.parameters.all-accounts.${this.scenarioFlavor}`);
  }

  submit() {
    if (this.duplicate) {
      this.basicService.showSpinner();
      this.scenariosService
        .copyScenario(this.scenario, this.form.value)
        .pipe(finalize(() => this.basicService.hideSpinner()))
        .subscribe({
          next: ({ new_scenario_id }: { new_scenario_id: number }) => {
            this.customRouter.go(`auth.alcmeon.fuse.parameters.account.${this.scenarioFlavor}-details`, {
              siteId: this.scenario.site_id,
              scenarioId: new_scenario_id,
            });
          },
        });
    } else {
      this.basicService.showSpinner();
      this.scenariosService
        .createScenario({
          ...this.form.value,
          selectedScenarioTemplate: this.selectedScenarioTemplate === 'blank' ? null : this.selectedScenarioTemplate,
        })
        .pipe(finalize(() => this.basicService.hideSpinner()))
        .subscribe({
          next: (scenario: Scenario) => this.scenariosService.editScenario(scenario),
        });
    }
  }

  showPreview(template: ScenarioTemplate) {
    if (template?.previewURL) {
      const newWindow = window.open('', '_blank');
      newWindow.document.write(`
  <html>
    <head>
      <title>Image Preview</title>
    </head>
    <body>
      <img src="${template.preview || (template.id === 'blank' && blankScenarioTemplatePreview)}" alt="Image Preview" />
    </body>
  </html>
`);
    }
  }

  private uniqueNamePerAccountValidator(siteCtrl, namesBySiteId: { [key: number]: string[] }) {
    const namesForSelectedSite: string[] = namesBySiteId[siteCtrl.value];
    this.warningNameAlreadyExists = namesForSelectedSite && namesForSelectedSite.includes(this.form.value.scenarioName);
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
